import { AlertMessage, Button, FileUploadField } from "@/components/reusable";
import { ProviderSelector } from "@/components/providers/ProviderSelector";
import { SchemaFilePreviewDialog } from "@/components/schema-builder/SchemaFilePreviewDialog";
import { deriveSchemaNameFromFileName, isSchemaNameTaken } from "@/lib/schemaBuilderHelpers";
import { uploadSchemaBuilderFile } from "@/services/api";
import { parseFile } from "@/services/fileParser";
import { listSchemas } from "@/services/schemaBuilderApi";
import type { ParsedWorksheet } from "@/types";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export function SchemaCreateFromFilePage() {
  const navigate = useNavigate();

  const [file, setFile] = useState<File | null>(null);
  const [schemaName, setSchemaName] = useState("");
  const [isNameAutoFilled, setIsNameAutoFilled] = useState(true);
  const [providerId, setProviderId] = useState<string | null>(null);
  const [error, setError] = useState("");

  const [showPreview, setShowPreview] = useState(false);
  const [worksheets, setWorksheets] = useState<ParsedWorksheet[]>([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const canStart = file !== null && schemaName.trim().length > 0 && providerId !== null;

  function handleFileSelect(selectedFile: File | null) {
    setFile(selectedFile);
    setError("");
    // Only auto-fill the name from the file — never clobber a name the user typed themselves.
    if (isNameAutoFilled) {
      setSchemaName(selectedFile ? deriveSchemaNameFromFileName(selectedFile.name) : "");
    }
  }

  function handleUploadError(message: string) {
    setError(message);
  }

  async function handleStartBuilding() {
    if (!file || !schemaName.trim()) return;

    setIsParsing(true);
    setError("");

    try {
      const schemas = await listSchemas(true);
      if (isSchemaNameTaken(schemaName, schemas)) {
        setError("A schema with this name already exists. Please choose a different name.");
        return;
      }

      const parsed = await parseFile(file);
      setWorksheets(parsed);
      setShowPreview(true);
    } catch (err) {
      setError(`Failed to parse file: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsParsing(false);
    }
  }

  function handlePreviewCancel() {
    setShowPreview(false);
  }

  async function handleConfirmUpload() {
    if (!file) return;
    setIsUploading(true);
    try {
      const fileId = await uploadSchemaBuilderFile(file);
      navigate("/schema-builder/new", {
        state: {
          source: "upload",
          schemaName,
          fileName: file.name,
          fileId,
          providerId,
        },
      });
    } catch (err) {
      setError(`Failed to upload file: ${err instanceof Error ? err.message : String(err)}`);
      setIsUploading(false);
      setShowPreview(false);
    }
  }

  return (
    <div className="min-h-[calc(100vh-72px)] animate-fade-in">
      <section className="max-w-xl px-12 py-10">
        <button
          type="button"
          onClick={() => navigate("/schema-builder/create")}
          className="mb-8 flex items-center gap-2 text-sm font-medium text-[var(--color-text-primary)] transition hover:text-[var(--color-brand-primary)]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <h1
          className="mb-10 text-4xl leading-tight text-[var(--color-text-primary)]"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Build Schema from File
        </h1>

        <div className="space-y-6">
          <div>
            <label className="mb-2 block text-sm font-semibold text-[var(--color-text-primary)]">
              Provider
            </label>
            <ProviderSelector value={providerId} onChange={setProviderId} />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-[var(--color-text-primary)]">
              Upload Source File
            </label>

            <FileUploadField
              file={file}
              onFileSelect={handleFileSelect}
              onError={handleUploadError}
              disabled={!providerId}
            />
            {!providerId && (
              <p className="mt-1.5 text-xs text-[var(--color-text-tertiary)]">
                Select a provider first.
              </p>
            )}
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-[var(--color-text-primary)]">
              Name Your Schema
            </label>

            <input
              value={schemaName}
              onChange={(event) => {
                setSchemaName(event.target.value);
                setIsNameAutoFilled(false);
                setError("");
              }}
              placeholder="Schema Name ABC"
              className="h-11 w-full rounded-md border border-[var(--color-border-light)] bg-white px-3 text-sm text-[var(--color-text-primary)] outline-none placeholder:text-[var(--color-text-tertiary)] focus:border-[var(--color-brand-primary)]"
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={handleStartBuilding} disabled={!canStart || isParsing} size="lg">
              {isParsing ? "Loading..." : "Start Building"}
            </Button>
          </div>

          {error && <AlertMessage variant="error" message={error} />}
        </div>
      </section>

      <SchemaFilePreviewDialog
        open={showPreview}
        onOpenChange={setShowPreview}
        fileName={file?.name ?? ""}
        worksheets={worksheets}
        onConfirm={handleConfirmUpload}
        onCancel={handlePreviewCancel}
        isUploading={isUploading}
      />
    </div>
  );
}
